import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ClipboardList, Dumbbell, FileDown, Pencil, Play, Plus, Trash2 } from "lucide-react";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import GuestPlayerPicker from "@/components/GuestPlayerPicker";
import { toast } from "sonner";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import TaskImageUpload from "@/components/TaskImageUpload";
import { TaskTemplateButton } from "@/components/TaskTemplatePicker";
import { CopySessionPicker } from "@/components/CopySessionPicker";
import TacticalBoard from "@/components/TacticalBoard";
import YouTubeThumbnail from "@/components/YouTubeThumbnail";
import { exportTrainingPdf } from "@/lib/export";

const STATUSES = [
  { v: "asiste", label: "Asiste", color: "bg-emerald-500 text-white" },
  { v: "retraso", label: "Retraso", color: "bg-yellow-500 text-slate-900" },
  { v: "no_asiste", label: "No asiste", color: "bg-red-500 text-white" },
  { v: "enfermo", label: "Enfermo", color: "bg-amber-500 text-white" },
  { v: "lesion", label: "Lesión", color: "bg-rose-600 text-white" },
  { v: "motivos_personales", label: "Motivos personales", color: "bg-slate-600 text-white" },
];

function AnimButton({ boardData, onView, slotKey }) {
  if ((boardData?.scenes?.length || 0) < 2) return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
      onClick={onView}
      data-testid={`slot-view-anim-${slotKey}`}
    >
      <Play className="h-3.5 w-3.5 mr-1.5" /> Ver animación
    </Button>
  );
}


export default function TrainingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [training, setTraining] = useState(null);
  const [players, setPlayers] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [lateMap, setLateMap] = useState({});
  const [staff, setStaff] = useState([]);
  const [staffStatusMap, setStaffStatusMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("asistencia");
  const [teamName, setTeamName] = useState("");
  // boardOpenKey: null when closed; "warmup" | "main_1" | "main_2" | "main_3" | "cooldown"
  const [boardOpenKey, setBoardOpenKey] = useState(null);
  // viewAnimKey: slot key whose saved animation is being viewed
  const [viewAnimKey, setViewAnimKey] = useState(null);

  const TASK_SUFFIXES = ["", "_obj", "_image", "_video", "_board", "_min"];

  // Builds the local session state from a training doc, detecting dynamic tasks
  const buildSession = (t) => {
    let count = Math.max(3, parseInt(t.main_count, 10) || 3);
    while (TASK_SUFFIXES.some((suf) => t[`main_${count + 1}${suf}`])) count += 1;
    const s = {
      main_count: count,
      warmup: t.warmup || "",
      warmup_obj: t.warmup_obj || "",
      warmup_image: t.warmup_image || "",
      warmup_video: t.warmup_video || "",
      warmup_board: t.warmup_board || null,
      warmup_min: t.warmup_min || "",
      cooldown: t.cooldown || "",
      cooldown_obj: t.cooldown_obj || "",
      cooldown_image: t.cooldown_image || "",
      cooldown_video: t.cooldown_video || "",
      cooldown_board: t.cooldown_board || null,
      cooldown_min: t.cooldown_min || "",
    };
    for (let n = 1; n <= count; n++) {
      s[`main_${n}`] = t[`main_${n}`] || (n === 1 ? t.main || "" : "");
      s[`main_${n}_obj`] = t[`main_${n}_obj`] || (n === 1 ? t.main_obj || "" : "");
      s[`main_${n}_image`] = t[`main_${n}_image`] || "";
      s[`main_${n}_video`] = t[`main_${n}_video`] || "";
      s[`main_${n}_board`] = t[`main_${n}_board`] || null;
      s[`main_${n}_min`] = t[`main_${n}_min`] || "";
    }
    return s;
  };

  // Session plan local state (edits before save)
  const [session, setSession] = useState(() => buildSession({}));

  useEffect(() => {
    api.get(`/trainings/${id}/attendance`).then(async (r) => {
      setTraining(r.data.training);
      const t = r.data.training;
      setSession(buildSession(t));
      const pl = await api.get(`/players?team_id=${t.team_id}`);
      let merged = pl.data;
      if (t.guest_player_ids?.length) {
        const dir = await api.get("/players/club-directory").catch(() => ({ data: [] }));
        merged = [...pl.data, ...dir.data.filter((g) => t.guest_player_ids.includes(g.id)).map((g) => ({ ...g, _guest: true }))];
      }
      setPlayers(merged);
      api.get("/teams").then((ts) => {
        setTeamName((ts.data || []).find((x) => x.id === t.team_id)?.name || "");
      });
      const map = {};
      r.data.items.forEach((a) => (map[a.player_id] = a.status));
      const lm = {};
      r.data.items.forEach((a) => {
        if (a.status === "retraso") lm[a.player_id] = { minutes: a.late_minutes ?? "", reason: a.late_reason || "", justified: !!a.late_justified };
      });
      setLateMap(lm);
      setStatusMap(map);
      setStaff(r.data.staff || []);
      const smap = {};
      (r.data.staff_items || []).forEach((a) => (smap[a.user_id] = a.status));
      setStaffStatusMap(smap);
    });
  }, [id]);

  // Persist a single field of the session immediately (used for image updates)
  const persistField = async (patch) => {
    if (!training) return;
    const next = { ...session, ...patch };
    setSession(next);
    try {
      await api.put(`/trainings/${id}`, {
        team_id: training.team_id,
        date: training.date,
        notes: training.notes || "",
        main: "",
        main_obj: "",
        ...next,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // `key` is one of: "warmup", "main_1"..."main_N", "cooldown".
  const slotLabel = (key) =>
    key === "warmup" ? "calentamiento"
      : key === "cooldown" ? "vuelta a la calma"
      : `tarea ${key.split("_")[1]}`;

  const applyTemplateToSlot = (key, tpl) => {
    const patch = {
      [key]: tpl.description || "",
      [`${key}_obj`]: tpl.objective || "",
      [`${key}_board`]: tpl.board_data || null,
    };
    if (tpl.image_id) {
      patch[`${key}_image`] = tpl.image_id;
    }
    if (tpl.video_url) {
      patch[`${key}_video`] = tpl.video_url;
    }
    persistField(patch);
    toast.success(`${slotLabel(key)} cargada desde la biblioteca`);
  };

  const saveSlotToLibrary = async (key) => {
    const lbl = slotLabel(key);
    const defaultTitle = lbl.charAt(0).toUpperCase() + lbl.slice(1);
    const title = window.prompt("Título para la plantilla (ej. Rondo 5v2):", defaultTitle);
    if (!title) return false;
    const tagsRaw = window.prompt(
      "Etiquetas separadas por coma (opcional, ej. rondo, presión):",
      "",
    );
    const tags = (tagsRaw || "")
      .split(",")
      .map((t) => t.trim().toLowerCase().replace(/^#/, ""))
      .filter(Boolean);
    const share = window.confirm(
      "¿Compartir esta plantilla con todo el club?\n\n" +
      "Aceptar  → visible para todos los entrenadores.\n" +
      "Cancelar → privada de este equipo.",
    );
    const payload = {
      team_id: share ? null : (training?.team_id || null),
      title,
      description: session[key] || "",
      objective: session[`${key}_obj`] || "",
      image_id: session[`${key}_image`] || "",
      video_url: session[`${key}_video`] || "",
      board_data: session[`${key}_board`] || null,
      tags,
    };
    try {
      await api.post("/task-templates", payload);
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    }
  };

  const canEdit = user?.role === "admin" || (user?.team_ids || []).includes(training?.team_id);

  const toMin = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const totalMin = toMin(session.warmup_min) + toMin(session.cooldown_min) +
    Array.from({ length: session.main_count || 3 }, (_, i) => toMin(session[`main_${i + 1}_min`]))
      .reduce((a, b) => a + b, 0);

  const addGuest = async (p) => {
    await api.post(`/trainings/${id}/guests`, { player_id: p.id });
    setPlayers((ps) => [...ps, { ...p, _guest: true }]);
  };

  const removeGuest = async (p) => {
    try {
      await api.delete(`/trainings/${id}/guests/${p.id}`);
      setPlayers((ps) => ps.filter((x) => x.id !== p.id));
      setStatusMap((m) => { const n = { ...m }; delete n[p.id]; return n; });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveAttendance = async () => {
    setSaving(true);
    const items = players.map((p) => {
      const it = { player_id: p.id, status: statusMap[p.id] || "no_asiste" };
      if (it.status === "retraso") {
        const l = lateMap[p.id] || {};
        it.late_minutes = l.minutes !== "" && l.minutes != null ? parseInt(l.minutes, 10) || null : null;
        it.late_reason = l.reason || "";
        it.late_justified = l.justified ?? null;
      }
      return it;
    });
    try {
      await api.post(`/trainings/${id}/attendance`, { items });
      if (staff.length > 0) {
        await api.post(`/trainings/${id}/staff-attendance`, {
          items: staff.map((s) => ({
            user_id: s.user_id,
            status: staffStatusMap[s.user_id] || "no_asiste",
          })),
        });
      }
      toast.success("Asistencia guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveSession = async () => {
    setSaving(true);
    try {
      await api.put(`/trainings/${id}`, {
        team_id: training.team_id,
        date: training.date,
        time: training.time || "",
        notes: training.notes || "",
        // Persist legacy fields cleared (we now use main_1/2/3 exclusively)
        main: "",
        main_obj: "",
        ...session,
      });
      setTraining({ ...training, ...session });
      toast.success("Sesión guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copySessionFrom = async (t) => {
    const src = buildSession(t);
    // Clear extra slots the current session had beyond the copied one
    for (let n = src.main_count + 1; n <= (session.main_count || 3); n++) {
      TASK_SUFFIXES.forEach((suf) => { src[`main_${n}${suf}`] = suf === "_board" ? null : ""; });
    }
    setSession(src);
    try {
      await api.put(`/trainings/${id}`, {
        team_id: training.team_id,
        date: training.date,
        time: training.time || "",
        notes: training.notes || "",
        main: "",
        main_obj: "",
        ...src,
      });
      setTraining({ ...training, ...src });
      toast.success(`Sesión copiada del entrenamiento del ${t.date}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addTask = () => {
    const n = (session.main_count || 3) + 1;
    setSession({
      ...session, main_count: n,
      [`main_${n}`]: "", [`main_${n}_obj`]: "", [`main_${n}_image`]: "",
      [`main_${n}_video`]: "", [`main_${n}_board`]: null, [`main_${n}_min`]: "",
    });
  };

  const removeTask = async (n) => {
    if (!window.confirm(`¿Eliminar la tarea ${n}?`)) return;
    const c = session.main_count;
    const next = { ...session };
    for (let k = n; k < c; k++) {
      TASK_SUFFIXES.forEach((suf) => { next[`main_${k}${suf}`] = next[`main_${k + 1}${suf}`]; });
    }
    TASK_SUFFIXES.forEach((suf) => { next[`main_${c}${suf}`] = suf === "_board" ? null : ""; });
    next.main_count = c - 1;
    setSession(next);
    try {
      await api.put(`/trainings/${id}`, {
        team_id: training.team_id,
        date: training.date,
        time: training.time || "",
        notes: training.notes || "",
        main: "",
        main_obj: "",
        ...next,
      });
      setTraining({ ...training, ...next });
      toast.success(`Tarea ${n} eliminada`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bulk = (val) => {
    const map = {};
    players.forEach((p) => (map[p.id] = val));
    setStatusMap(map);
    const smap = {};
    staff.forEach((s) => (smap[s.user_id] = val));
    setStaffStatusMap(smap);
  };

  if (!training) return <div className="text-slate-500 text-sm">Cargando...</div>;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate("/trainings")} data-testid="back-button">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver
      </Button>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
          Entrenamiento{teamName ? ` · ${teamName}` : ""}
        </div>
        <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>
          {training.date}{training.time ? ` · ${training.time}` : ""}
        </h1>
        <select
          value={["PRESEASON", "COMPETITION"].includes(training.season_phase) ? training.season_phase : "UNASSIGNED"}
          onChange={async (e) => {
            try {
              await api.post(`/trainings/${training.id}/season-phase`, { season_phase: e.target.value });
              setTraining((t) => ({ ...t, season_phase: e.target.value }));
              toast.success("Fase actualizada — estadísticas recalculadas");
            } catch (err) { toast.error(formatApiError(err)); }
          }}
          className={`inline-block mt-1 mr-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold border-0 cursor-pointer ${["PRESEASON", "COMPETITION"].includes(training.season_phase) ? (training.season_phase === "PRESEASON" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700") : "bg-slate-100 text-slate-500"}`}
          data-testid="training-detail-phase-select"
        >
          {!["PRESEASON", "COMPETITION"].includes(training.season_phase) && <option value="UNASSIGNED" disabled>Fase pendiente</option>}
          <option value="PRESEASON">Pretemporada</option>
          <option value="COMPETITION">Competición</option>
        </select>
        {teamName && (
          <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded-full bg-purple-100 text-purple-800 text-xs font-bold" data-testid="training-detail-team-name">
            {teamName}
          </span>
        )}
        <p className="text-slate-500 text-sm mt-1">{training.notes || ""}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { key: "asistencia", label: "Asistencia", Icon: ClipboardList },
          { key: "sesion", label: "Sesión", Icon: Dumbbell },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 font-bold text-sm -mb-px border-b-2 transition ${
              tab === key
                ? "border-purple-600 text-purple-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`tab-${key}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "asistencia" && (
        <div className="space-y-4">
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <span className="text-xs uppercase tracking-widest text-slate-500 self-center mr-2">Marcar todos:</span>
              {STATUSES.map((s) => (
                <Button key={s.v} size="sm" variant="outline" onClick={() => bulk(s.v)} data-testid={`bulk-${s.v}`}>
                  {s.label}
                </Button>
              ))}
            </div>
          )}

          {staff.length > 0 && (
            <Card className="border-purple-200 shadow-sm" data-testid="staff-attendance-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-black uppercase tracking-widest text-purple-700" style={{ fontFamily: "Nunito" }}>
                  Cuerpo técnico
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y divide-slate-100">
                {staff.map((s) => (
                  <div key={s.user_id} className="p-4 flex flex-col gap-2.5" data-testid={`staff-attendance-row-${s.user_id}`}>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex px-2 py-0.5 shrink-0 rounded-full bg-purple-100 text-purple-800 font-bold text-[10px] uppercase tracking-wider">
                        {s.role}
                      </span>
                      <div className="font-semibold whitespace-nowrap">{s.name}</div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {STATUSES.map((st) => (
                        <button
                          key={st.v}
                          type="button"
                          disabled={!canEdit}
                          onClick={() => setStaffStatusMap((m) => ({ ...m, [s.user_id]: st.v }))}
                          className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                            staffStatusMap[s.user_id] === st.v ? st.color : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          } ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                          data-testid={`staff-attendance-${s.user_id}-${st.v}`}
                        >
                          {st.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {canEdit && (
            <GuestPlayerPicker excludeIds={players.map((p) => p.id)} onAdd={addGuest} />
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 divide-y divide-slate-100">
              {players.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No hay jugadores.</div>}
              {players.map((p) => (
                <div key={p.id} className="p-4 flex flex-col gap-2.5" data-testid={`attendance-row-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <PlayerAvatar player={p} />
                    <div>
                      <div className="font-semibold whitespace-nowrap">{p.name}</div>
                      <div className="text-xs text-slate-500">
                        {p.position || ""}
                        {p._guest && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">Invitado · {p.team_name || "otro equipo"}</span>}
                      </div>
                    </div>
                    {p._guest && canEdit && (
                      <button type="button" onClick={() => removeGuest(p)} className="ml-auto text-slate-400 hover:text-rose-600 text-xs font-bold" data-testid={`guest-remove-${p.id}`}>
                        Quitar
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {STATUSES.map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        disabled={!canEdit}
                        onClick={() => setStatusMap((m) => ({ ...m, [p.id]: s.v }))}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                          statusMap[p.id] === s.v ? s.color : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        } ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                        data-testid={`attendance-${p.id}-${s.v}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  {statusMap[p.id] === "retraso" && (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-yellow-50 border border-yellow-200 p-2" data-testid={`late-detail-${p.id}`}>
                      <input
                        type="number" min="0" max="120" placeholder="Min. retraso"
                        className="w-24 h-8 rounded-md border border-yellow-300 px-2 text-xs"
                        value={lateMap[p.id]?.minutes ?? ""}
                        disabled={!canEdit}
                        onChange={(e) => setLateMap((m) => ({ ...m, [p.id]: { ...(m[p.id] || {}), minutes: e.target.value } }))}
                        data-testid={`late-minutes-${p.id}`}
                      />
                      <input
                        type="text" placeholder="Motivo (opcional)"
                        className="flex-1 min-w-[140px] h-8 rounded-md border border-yellow-300 px-2 text-xs"
                        value={lateMap[p.id]?.reason ?? ""}
                        disabled={!canEdit}
                        onChange={(e) => setLateMap((m) => ({ ...m, [p.id]: { ...(m[p.id] || {}), reason: e.target.value } }))}
                        data-testid={`late-reason-${p.id}`}
                      />
                      <label className="flex items-center gap-1.5 text-xs font-bold text-yellow-800">
                        <input
                          type="checkbox"
                          checked={!!lateMap[p.id]?.justified}
                          disabled={!canEdit}
                          onChange={(e) => setLateMap((m) => ({ ...m, [p.id]: { ...(m[p.id] || {}), justified: e.target.checked } }))}
                          data-testid={`late-justified-${p.id}`}
                        />
                        Justificado
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {canEdit && (
            <div className="flex justify-end">
              <Button onClick={saveAttendance} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="save-attendance-button">
                {saving ? "Guardando..." : "Guardar asistencia"}
              </Button>
            </div>
          )}
        </div>
      )}

      {tab === "sesion" && (
        <div className="space-y-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-lg font-black" style={{ fontFamily: "Nunito" }}>
                    Planificación de la sesión
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    Fecha: <span className="font-bold text-slate-700">{training.date}</span>
                  </p>
                  {totalMin > 0 && (
                    <p className="text-xs text-purple-700 font-bold mt-0.5" data-testid="session-total-duration">
                      Duración total: {totalMin} min
                    </p>
                  )}
                </div>
                {canEdit && (
                  <CopySessionPicker
                    teamId={training.team_id}
                    currentId={training.id}
                    onCopy={copySessionFrom}
                  />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Calentamiento */}
              <div className="space-y-2 pb-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-bold text-slate-700">Calentamiento</Label>
                    <Input
                      type="number"
                      min="0"
                      value={session.warmup_min || ""}
                      onChange={(e) => setSession({ ...session, warmup_min: e.target.value })}
                      disabled={!canEdit}
                      placeholder="min"
                      className="w-16 h-7 text-xs px-2"
                      data-testid="session-warmup-min"
                    />
                    <span className="text-[10px] text-slate-400">min</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <AnimButton
                      boardData={session.warmup_board}
                      slotKey="warmup"
                      onView={() => setViewAnimKey("warmup")}
                    />
                    {canEdit && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                          onClick={() => setBoardOpenKey("warmup")}
                          data-testid="open-board-warmup"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Pizarra
                        </Button>
                        <TaskTemplateButton
                          onApply={(tpl) => applyTemplateToSlot("warmup", tpl)}
                          saveCurrent={() => saveSlotToLibrary("warmup")}
                        />
                      </>
                    )}
                  </div>
                </div>
                <Textarea
                  rows={3}
                  value={session.warmup}
                  onChange={(e) => setSession({ ...session, warmup: e.target.value })}
                  disabled={!canEdit}
                  placeholder="Describe el calentamiento..."
                  data-testid="session-warmup"
                />
                <Label className="text-xs uppercase tracking-widest text-slate-500 pt-1">Objetivo</Label>
                <Input
                  value={session.warmup_obj}
                  onChange={(e) => setSession({ ...session, warmup_obj: e.target.value })}
                  disabled={!canEdit}
                  placeholder="Objetivo de esta parte"
                  data-testid="session-warmup-obj"
                />
                <TaskImageUpload
                  trainingId={training.id}
                  taskKey="warmup"
                  imageId={session.warmup_image}
                  canEdit={canEdit}
                  onChange={(newId) => persistField({ warmup_image: newId })}
                />
                <div className="flex items-center gap-2 pt-1">
                  <Label className="text-xs text-slate-500 shrink-0">URL de vídeo (YouTube)</Label>
                  <Input
                    type="url"
                    value={session.warmup_video || ""}
                    onChange={(e) => setSession({ ...session, warmup_video: e.target.value })}
                    onBlur={() => persistField({ warmup_video: session.warmup_video || "" })}
                    disabled={!canEdit}
                    placeholder="https://youtu.be/..."
                    data-testid="session-warmup-video"
                  />
                  <YouTubeThumbnail url={session.warmup_video} data-testid="session-warmup-video-preview" />
                </div>
              </div>

              {/* Parte principal - tareas dinámicas */}
              <div className="space-y-4 pb-4 border-b border-slate-100">
                <div>
                  <Label className="text-sm font-bold text-slate-700">Parte principal</Label>
                  <p className="text-xs text-slate-500">Añade tantas tareas como necesites.</p>
                </div>
                {Array.from({ length: session.main_count || 3 }, (_, i) => i + 1).map((n) => (
                  <div key={n} className="space-y-2 rounded-md border border-purple-100 bg-purple-50/30 p-3" data-testid={`session-main-task-${n}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs uppercase tracking-widest text-purple-700 font-bold">
                          Tarea {n}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={session[`main_${n}_min`] || ""}
                          onChange={(e) => setSession({ ...session, [`main_${n}_min`]: e.target.value })}
                          disabled={!canEdit}
                          placeholder="min"
                          className="w-16 h-7 text-xs px-2"
                          data-testid={`session-main_${n}-min`}
                        />
                        <span className="text-[10px] text-slate-400">min</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <AnimButton
                          boardData={session[`main_${n}_board`]}
                          slotKey={`main_${n}`}
                          onView={() => setViewAnimKey(`main_${n}`)}
                        />
                        {canEdit && (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                              onClick={() => setBoardOpenKey(`main_${n}`)}
                              data-testid={`open-board-${n}`}
                            >
                              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Pizarra
                            </Button>
                            <TaskTemplateButton
                              onApply={(tpl) => applyTemplateToSlot(`main_${n}`, tpl)}
                              saveCurrent={() => saveSlotToLibrary(`main_${n}`)}
                            />
                            {n > 3 && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="text-xs text-red-500 hover:bg-red-50 hover:text-red-600 px-2"
                                onClick={() => removeTask(n)}
                                title={`Eliminar tarea ${n}`}
                                data-testid={`remove-task-${n}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <Textarea
                      rows={2}
                      value={session[`main_${n}`]}
                      onChange={(e) => setSession({ ...session, [`main_${n}`]: e.target.value })}
                      disabled={!canEdit}
                      placeholder={`Descripción de la tarea ${n}...`}
                      data-testid={`session-main_${n}`}
                    />
                    <Input
                      value={session[`main_${n}_obj`]}
                      onChange={(e) => setSession({ ...session, [`main_${n}_obj`]: e.target.value })}
                      disabled={!canEdit}
                      placeholder="Objetivo de la tarea"
                      data-testid={`session-main_${n}-obj`}
                    />
                    <TaskImageUpload
                      trainingId={training.id}
                      taskN={n}
                      imageId={session[`main_${n}_image`]}
                      canEdit={canEdit}
                      onChange={(newId) => persistField({ [`main_${n}_image`]: newId })}
                    />
                    <div className="flex items-center gap-2 pt-1">
                      <Label className="text-xs text-slate-500 shrink-0">URL de vídeo (YouTube)</Label>
                      <Input
                        type="url"
                        value={session[`main_${n}_video`] || ""}
                        onChange={(e) => setSession({ ...session, [`main_${n}_video`]: e.target.value })}
                        onBlur={() => persistField({ [`main_${n}_video`]: session[`main_${n}_video`] || "" })}
                        disabled={!canEdit}
                        placeholder="https://youtu.be/..."
                        data-testid={`session-main_${n}-video`}
                      />
                      <YouTubeThumbnail url={session[`main_${n}_video`]} data-testid={`session-main_${n}-video-preview`} />
                    </div>
                  </div>
                ))}
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTask}
                    className="w-full border-dashed border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
                    data-testid="add-task-button"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Añadir tarea
                  </Button>
                )}
              </div>

              {/* Vuelta a la calma */}
              <div className="space-y-2 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-bold text-slate-700">Vuelta a la calma</Label>
                    <Input
                      type="number"
                      min="0"
                      value={session.cooldown_min || ""}
                      onChange={(e) => setSession({ ...session, cooldown_min: e.target.value })}
                      disabled={!canEdit}
                      placeholder="min"
                      className="w-16 h-7 text-xs px-2"
                      data-testid="session-cooldown-min"
                    />
                    <span className="text-[10px] text-slate-400">min</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <AnimButton
                      boardData={session.cooldown_board}
                      slotKey="cooldown"
                      onView={() => setViewAnimKey("cooldown")}
                    />
                    {canEdit && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                          onClick={() => setBoardOpenKey("cooldown")}
                          data-testid="open-board-cooldown"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Pizarra
                        </Button>
                        <TaskTemplateButton
                          onApply={(tpl) => applyTemplateToSlot("cooldown", tpl)}
                          saveCurrent={() => saveSlotToLibrary("cooldown")}
                        />
                      </>
                    )}
                  </div>
                </div>
                <Textarea
                  rows={3}
                  value={session.cooldown}
                  onChange={(e) => setSession({ ...session, cooldown: e.target.value })}
                  disabled={!canEdit}
                  placeholder="Describe la vuelta a la calma..."
                  data-testid="session-cooldown"
                />
                <Label className="text-xs uppercase tracking-widest text-slate-500 pt-1">Objetivo</Label>
                <Input
                  value={session.cooldown_obj}
                  onChange={(e) => setSession({ ...session, cooldown_obj: e.target.value })}
                  disabled={!canEdit}
                  placeholder="Objetivo de esta parte"
                  data-testid="session-cooldown-obj"
                />
                <TaskImageUpload
                  trainingId={training.id}
                  taskKey="cooldown"
                  imageId={session.cooldown_image}
                  canEdit={canEdit}
                  onChange={(newId) => persistField({ cooldown_image: newId })}
                />
                <div className="flex items-center gap-2 pt-1">
                  <Label className="text-xs text-slate-500 shrink-0">URL de vídeo (YouTube)</Label>
                  <Input
                    type="url"
                    value={session.cooldown_video || ""}
                    onChange={(e) => setSession({ ...session, cooldown_video: e.target.value })}
                    onBlur={() => persistField({ cooldown_video: session.cooldown_video || "" })}
                    disabled={!canEdit}
                    placeholder="https://youtu.be/..."
                    data-testid="session-cooldown-video"
                  />
                  <YouTubeThumbnail url={session.cooldown_video} data-testid="session-cooldown-video-preview" />
                </div>
              </div>

              <div className="pt-2 flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => exportTrainingPdf(training)}
                  data-testid="export-training-pdf"
                >
                  <FileDown className="h-4 w-4 mr-2" /> PDF de la sesión
                </Button>
                {canEdit && (
                  <Button onClick={saveSession} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="save-session-button">
                    {saving ? "Guardando..." : "Guardar sesión"}
                  </Button>
                )}
              </div>
              <div className="pt-4 border-t border-slate-100">
                <AttachmentsPanel
                  scope="training"
                  scopeId={training.id}
                  canEdit={canEdit}
                  title="Archivos de la sesión"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tactical board modal */}
      <TacticalBoard
        open={boardOpenKey !== null}
        onOpenChange={(o) => !o && setBoardOpenKey(null)}
        trainingId={training?.id}
        taskKey={boardOpenKey || "main_1"}
        teamId={training?.team_id}
        defaultDescription={
          boardOpenKey === "warmup" ? session.warmup
            : boardOpenKey === "cooldown" ? session.cooldown
            : boardOpenKey ? session[boardOpenKey] : ""
        }
        defaultObjective={
          boardOpenKey === "warmup" ? session.warmup_obj
            : boardOpenKey === "cooldown" ? session.cooldown_obj
            : boardOpenKey ? session[`${boardOpenKey}_obj`] : ""
        }
        onSaved={(attId, meta) => {
          const k = boardOpenKey;
          if (!k) return;
          const imageField = `${k}_image`; // warmup_image, main_1_image, cooldown_image
          const patch = { [imageField]: attId, [`${k}_board`]: meta?.boardData || null };
          const descField = k;             // warmup, main_1, cooldown
          const objField = `${k}_obj`;     // warmup_obj, main_1_obj, cooldown_obj
          if (meta?.description) patch[descField] = meta.description;
          if (meta?.objective) patch[objField] = meta.objective;
          persistField(patch);
        }}
        initialBoardData={boardOpenKey ? session[`${boardOpenKey}_board`] : null}
      />
      {viewAnimKey && (
        <TacticalBoard
          open
          onOpenChange={(o) => !o && setViewAnimKey(null)}
          imageOnly
          viewOnly
          initialBoardData={session[`${viewAnimKey}_board`]}
        />
      )}
    </div>
  );
}

